"use client";

/**
 * HoldToUndo — press and hold for 6 seconds to undo a pending vote.
 *
 * Props:
 *   label        — text shown inside the ring (e.g. "Hold to undo")
 *   onUndo       — async callback; called after the 6s hold + confirm flash
 *   disabled     — prevents interaction while a transition is pending
 *   size         — ring diameter in px (default 80)
 *   colorClass   — Tailwind stroke colour class applied to the progress arc (default "stroke-yellow-400")
 */

import { useState, useCallback, useRef } from "react";
import { useLongPress } from "../../lib/use-long-press";

interface HoldToUndoProps {
  label?: string;
  onUndo: () => void | Promise<void>;
  disabled?: boolean;
  size?: number;
  colorClass?: string;
}

export default function HoldToUndo({
  label = "Hold to undo",
  onUndo,
  disabled = false,
  size = 80,
  colorClass = "stroke-yellow-400",
}: HoldToUndoProps) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"idle" | "holding" | "shake" | "confirming" | "done">("idle");
  const undoCalledRef = useRef(false);

  const handleComplete = useCallback(async () => {
    if (undoCalledRef.current) return;
    undoCalledRef.current = true;
    setProgress(1);
    setPhase("shake");
    // Brief shake phase (CSS animation runs for 400ms), then show confirm flash
    setTimeout(() => setPhase("confirming"), 420);
    setTimeout(async () => {
      setPhase("done");
      await onUndo();
    }, 820);
  }, [onUndo]);

  const handleCancel = useCallback(() => {
    if (phase === "shake" || phase === "confirming" || phase === "done") return;
    setProgress(0);
    setPhase("idle");
  }, [phase]);

  const { handlers } = useLongPress({
    duration: 6000,
    onProgress: (p) => {
      if (phase !== "shake" && phase !== "confirming" && phase !== "done") {
        setProgress(p);
        setPhase(p > 0 ? "holding" : "idle");
      }
    },
    onComplete: handleComplete,
    onCancel: handleCancel,
  });

  // SVG ring math
  const r = (size / 2) * 0.78;        // ring radius — leaves room for stroke
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - progress);

  const isShaking = phase === "shake";
  const isConfirming = phase === "confirming";
  const isDone = phase === "done";

  return (
    <button
      {...handlers}
      disabled={disabled || isDone}
      aria-label={label}
      title={label}
      className={[
        "relative select-none touch-none rounded-full transition-opacity",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/60",
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer active:scale-95",
        isShaking ? "animate-bnc-shake" : "",
      ].join(" ")}
      style={{ width: size, height: size, WebkitUserSelect: "none", userSelect: "none" }}
    >
      {/* Background track */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        className="absolute inset-0"
      >
        {/* Track ring */}
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          strokeWidth={4}
          className="stroke-white/15"
        />
        {/* Progress arc — rotated so it starts at top */}
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className={[
            colorClass,
            "transition-[stroke-dashoffset] duration-75",
            isConfirming || isDone ? "stroke-green-400" : "",
          ].join(" ")}
          style={{ transform: `rotate(-90deg)`, transformOrigin: `${cx}px ${cx}px` }}
        />
      </svg>

      {/* Inner content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 pointer-events-none">
        {isDone || isConfirming ? (
          <>
            <span className="text-xl leading-none">✓</span>
            <span className="text-[10px] font-black text-green-300 uppercase tracking-widest leading-none">
              Undone
            </span>
          </>
        ) : (
          <>
            <span className="text-lg leading-none">{progress > 0.05 ? "🔓" : "🔒"}</span>
            <span
              className={[
                "text-[9px] font-black uppercase tracking-wider leading-none text-center px-1",
                progress > 0.5 ? "text-yellow-300" : "text-white/50",
              ].join(" ")}
            >
              {progress > 0 ? "keep holding" : "hold to\nundo"}
            </span>
          </>
        )}
      </div>
    </button>
  );
}
